import React, { useEffect, useState } from "react";
import {
    AlertCircle,
    CheckCircle2,
    Cloud,
    Link2,
    Loader2,
    Mail,
    Power,
    RefreshCw,
    Save,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AiPageShell from "../../components/ai/AiPageShell";
import EpostaDetailPanel from "../../components/eposta/EpostaDetailPanel";
import EpostaKomutaDashboard from "../../components/eposta/EpostaKomutaDashboard";
import EpostaGidenPanel from "../../components/eposta/EpostaGidenPanel";
import EpostaKurallarPanel from "../../components/eposta/EpostaKurallarPanel";
import { ChevronDown, ChevronUp, Inbox, Search } from "lucide-react";
import { epostaMerkez } from "../../lib/api";

const PAGE_TABS = [
    { id: "komuta", label: "Komuta" },
    { id: "gelen", label: "Gelen" },
    { id: "giden", label: "Giden" },
    { id: "kurallar", label: "Kurallar" },
];

const TABS = [
    { key: "PENDING", label: "Bekleyen" },
    { key: "MATCHED", label: "Eşleşen" },
    { key: "IGNORED", label: "Yok sayılan" },
    { key: "takip", label: "Takip defteri" },
];

function SirketBadge({ name }) {
    if (!name) return null;
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-500/20 text-indigo-200 border border-indigo-500/30">
            {name}
        </span>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className="rounded-lg border border-white/10 bg-[#0B0F19]/80 px-4 py-3 min-w-[100px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`text-xl font-semibold mt-1 ${accent || "text-slate-100"}`}>{value ?? "—"}</div>
        </div>
    );
}

export default function EpostaMerkezPage() {
    const [pageTab, setPageTab] = useState("komuta");
    const [tab, setTab] = useState("PENDING");
    const [items, setItems] = useState([]);
    const [takipItems, setTakipItems] = useState([]);
    const [ozet, setOzet] = useState(null);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [fromEmail, setFromEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(null);
    const [matchInputs, setMatchInputs] = useState({});
    const [applyBusy, setApplyBusy] = useState({});
    const [oauthBusy, setOauthBusy] = useState(false);
    const [oauthCode, setOauthCode] = useState(null);
    const [syncToggleBusy, setSyncToggleBusy] = useState(false);
    const [azureClientInput, setAzureClientInput] = useState("");
    const [azureConfigured, setAzureConfigured] = useState(false);
    const [azureSaveBusy, setAzureSaveBusy] = useState(false);
    const [tenantInput, setTenantInput] = useState("");
    const [mailboxFilter, setMailboxFilter] = useState("all");
    const [syncHotmailBusy, setSyncHotmailBusy] = useState(false);
    const [syncGmailBusy, setSyncGmailBusy] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [searchQ, setSearchQ] = useState("");
    const [settingsOpen, setSettingsOpen] = useState(false);

    const loadOzet = () => {
        epostaMerkez.ozet().then(setOzet).catch(() => setOzet(null));
    };

    const load = () => {
        setListError(null);
        setListLoading(true);
        if (tab === "takip") {
            epostaMerkez
                .takipKayitlari()
                .then((d) => setTakipItems(d.items || []))
                .catch((err) => {
                    setTakipItems([]);
                    setListError(err?.response?.data?.detail || "Takip listesi yüklenemedi.");
                })
                .finally(() => setListLoading(false));
            return;
        }
        epostaMerkez
            .list(tab, mailboxFilter, searchQ)
            .then((d) => setItems(d.items || []))
            .catch((err) => {
                setItems([]);
                setListError(err?.response?.data?.detail || "E-posta listesi yüklenemedi.");
            })
            .finally(() => setListLoading(false));
    };

    useEffect(() => {
        loadOzet();
        epostaMerkez
            .azureClientGet()
            .then((d) => {
                setAzureConfigured(!!d.configured);
                if (d.client_id) setAzureClientInput(d.client_id);
                if (d.tenant_id) setTenantInput(d.tenant_id);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        load();
    }, [tab, mailboxFilter, searchQ]);

    async function handleToggleSync() {
        const next = !ozet?.sync_enabled;
        setSyncToggleBusy(true);
        try {
            await epostaMerkez.syncAyarSet(next);
            toast.success(next ? "Otomatik senkron açıldı." : "Otomatik senkron kapatıldı.");
            loadOzet();
        } catch {
            toast.error("Ayar kaydedilemedi.");
        } finally {
            setSyncToggleBusy(false);
        }
    }


    async function handleSaveAzureClient(e) {
        e?.preventDefault?.();
        const cid = azureClientInput.trim();
        if (!cid) {
            toast.error("Azure Client ID girin.");
            return;
        }
        setAzureSaveBusy(true);
        try {
            await epostaMerkez.azureClientSet(cid, tenantInput.trim());
            setAzureConfigured(true);
            toast.success("Client ID sunucuya kaydedildi. Simdi Hotmail baglayin.");
            loadOzet();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kaydedilemedi.");
        } finally {
            setAzureSaveBusy(false);
        }
    }

    async function handleSifirla() {
        if (!window.confirm('Baglanti sifirlansin mi?')) return;
        try {
            await epostaMerkez.sifirla();
            setAzureClientInput('');
            setAzureConfigured(false);
            toast.success('Sifirlandi');
            loadOzet();
        } catch { toast.error('Sifirlanamadi'); }
    }

    async function handleHotmailBagla() {
        if (!azureConfigured) {
            toast.error("Önce Azure Client ID kaydedin.");
            return;
        }
        setOauthBusy(true);
        setOauthCode(null);
        try {
            const start = await epostaMerkez.oauthBaslat();
            if (start.already_connected || start.graph_oauth_ready) {
                toast.success("Hotmail zaten bağlı.");
                loadOzet();
                return;
            }
            setOauthCode({
                user_code: start.user_code,
                uri: start.verification_uri || "https://login.microsoft.com/device",
                message: start.message,
            });
            toast.success(`Kod: ${start.user_code} — Microsoft sayfası açılıyor`, { duration: 12000 });
            const msUrl = start.verification_uri || "https://login.microsoft.com/device";
            window.open(msUrl, "_blank", "noopener,noreferrer");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kod alınamadı.");
        } finally {
            setOauthBusy(false);
        }
    }

    async function handleOauthTamamla() {
        if (!oauthCode) {
            toast.error("Önce «Hotmail bağla» ile kod alın.");
            return;
        }
        setOauthBusy(true);
        try {
            const done = await epostaMerkez.oauthTamamla();
            if (done.ok) {
                toast.success("Hotmail bağlandı.");
                setOauthCode(null);
                loadOzet();
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bağlantı tamamlanamadı. Kodu girdikten sonra tekrar deneyin.");
        } finally {
            setOauthBusy(false);
        }
    }

    function toastSyncResult(label, res) {
        if (!res?.ok) {
            toast.error(res?.error || res?.detail || `${label} senkron başarısız`);
            return;
        }
        const promoted = res.promoted || 0;
        const processed = (res.processed || 0) + promoted;
        const parts = [
            `${processed} işlendi`,
            `${res.matched || 0} eşleşti`,
            `${res.skipped || 0} zaten kayıtlı`,
        ];
        if (promoted > 0) parts.push(`${promoted} Hotmail'e taşındı`);
        toast.success(
            `${label}: ${parts.join(", ")}` +
                (res.pool_total != null
                    ? ` (${res.search_mode || "IMAP"}: ${res.scanned || 0}/${res.pool_total})`
                    : ""),
            { duration: res.note || res.direct_error ? 9000 : 4000 },
        );
        if (res.note) toast.message(res.note, { duration: 10000 });
        if (res.direct_error && res.auth === "oauth_consent_required") {
            toast.error(
                "Hotmail OAuth izni eksik — doğrudan kutu için «Hotmail bağla» ile yeniden onaylayın.",
                { duration: 12000 },
            );
        }
    }

    async function runSync(provider, setBusy) {
        if (ozet?.sync_enabled === false) {
            toast.error("Otomatik senkron kapalı.");
            return;
        }
        setBusy(true);
        try {
            const res = await epostaMerkez.imapSync(provider);
            if (provider === "all" && res.hotmail && res.gmail) {
                toastSyncResult("Hotmail", res.hotmail);
                toastSyncResult("Gmail", res.gmail);
            } else {
                toastSyncResult(provider === "hotmail" ? "Hotmail" : "Gmail", res);
            }
            loadOzet();
            load();
        } catch (err) {
            const data = err?.response?.data || {};
            const msg = data.error || data.detail || err?.message || "Senkron isteği başarısız (ağ zaman aşımı olabilir).";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }

    async function handleImapSync() {
        setSyncLoading(true);
        await runSync("all", setSyncLoading);
    }

    async function handleSyncHotmail() {
        await runSync("hotmail", setSyncHotmailBusy);
    }

    async function handleSyncGmail() {
        await runSync("gmail", setSyncGmailBusy);
    }

    async function handleIngest(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await epostaMerkez.create({ subject, body, from_email: fromEmail });
            toast.success(
                res.match_status === "MATCHED"
                    ? "E-posta otomatik dosyaya bağlandı."
                    : "Kayıt oluşturuldu.",
            );
            setSubject("");
            setBody("");
            loadOzet();
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Parse hatası");
        } finally {
            setLoading(false);
        }
    }

    async function onaylaBaglaRow(id, dosyaId, dosyaNo) {
        try {
            const res = await epostaMerkez.onaylaBagla(id, dosyaId, dosyaNo);
            toast.success(res.metin || "Dosyaya bağlandı ve onaylandı.");
            loadOzet();
            load();
            if (selectedId === id) setSelectedId(id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Onay başarısız.");
        }
    }

    async function suggestRow(id) {
        try {
            await epostaMerkez.suggest(id);
            toast.success("Eşleşmeler yenilendi.");
            load();
        } catch {
            toast.error("Öneri alınamadı.");
        }
    }

    async function matchRow(id, dosyaId, dosyaNo) {
        try {
            await epostaMerkez.match(id, dosyaId, dosyaNo);
            toast.success("E-posta dosyaya bağlandı");
            loadOzet();
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Eşleme yapılamadı.");
        }
    }

    async function applyTalepNo(row) {
        const dosyaId = row.matched_dosya_id;
        if (!dosyaId) {
            toast.error("Önce dosyaya bağlayın.");
            return;
        }
        setApplyBusy((m) => ({ ...m, [row.id || row]: true }));
        try {
            const rid = row.id || row;
            const res = await epostaMerkez.apply(rid, dosyaId);
            toast.success(res.metin || "Talep no kaydedildi.");
            loadOzet();
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Talep no uygulanamadı.");
        } finally {
            setApplyBusy((m) => ({ ...m, [row.id]: false }));
        }
    }

    const oauthReady = ozet?.graph_oauth_ready;
    const oauthConsent = ozet?.oauth_consent_required || ozet?.mailboxes?.hotmail?.oauth_needs_consent;
    const oauthError = ozet?.oauth_error || ozet?.mailboxes?.hotmail?.oauth_error;
    const mailbox = ozet?.mailbox || "av.mervebilselcuk@hotmail.com";
    const syncOn = ozet?.sync_enabled !== false;

    return (
        <AiPageShell
            title="E-posta Merkezi"
            subtitle="Komuta merkezi, gelen kutusu, giden kuyruk ve şirket kuralları — tek panel."
            icon={Mail}
        >
            <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3 mb-4">
                {PAGE_TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setPageTab(t.id)}
                        className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${
                            pageTab === t.id
                                ? "bg-sky-500/15 text-sky-200 border border-sky-500/35"
                                : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {pageTab === "komuta" && (
                <EpostaKomutaDashboard onSync={handleImapSync} syncBusy={syncLoading} />
            )}

            {pageTab === "giden" && <EpostaGidenPanel />}

            {pageTab === "kurallar" && <EpostaKurallarPanel />}

            {pageTab === "gelen" && (
                <>
            <div className="rounded-xl border border-white/10 bg-[#121A2F]/50 p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-3 text-xs">
                    <StatCard label="Bekleyen" value={ozet?.pending} accent="text-amber-200" />
                    <StatCard label="Eşleşen" value={ozet?.matched} accent="text-emerald-200" />
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <button type="button" disabled={syncToggleBusy} onClick={handleToggleSync} className={`relative w-11 h-6 rounded-full ${syncOn ? "bg-emerald-600" : "bg-slate-600"}`}>
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${syncOn ? "translate-x-5" : ""}`} />
                    </button>
                    Otomatik senkron {syncOn ? "açık" : "kapalı"}
                </label>
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={handleSyncHotmail} disabled={syncHotmailBusy} className="h-9 px-3 rounded-lg bg-sky-600 text-white text-xs font-semibold inline-flex items-center gap-2">
                        {syncHotmailBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Hotmail
                    </button>
                    <button type="button" onClick={handleSyncGmail} disabled={syncGmailBusy} className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold inline-flex items-center gap-2">
                        {syncGmailBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Gmail
                    </button>
                    <button type="button" onClick={handleImapSync} disabled={syncLoading} className="h-9 px-4 rounded-lg bg-indigo-600 text-white text-xs font-semibold inline-flex items-center gap-2">
                        {syncLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Tümü
                    </button>
                </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-[#121A2F]/80 mb-4 overflow-hidden">
                <button
                    type="button"
                    onClick={() => setSettingsOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm text-slate-300 hover:bg-white/5"
                >
                    <span className="font-medium">Senkronizasyon ve manuel ekleme</span>
                    {settingsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {settingsOpen && (
                    <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
                        <p className="text-xs text-slate-500">Hotmail / Gmail kutuları ve manuel yapıştırma. Senkron bu alandan yönetilir.</p>
                        <form onSubmit={handleIngest} className="grid gap-2">
                            <input placeholder="Gönderen" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} className="h-9 rounded-md bg-[#0B0F19] border border-white/10 px-3 text-xs text-slate-200" />
                            <input placeholder="Konu" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 rounded-md bg-[#0B0F19] border border-white/10 px-3 text-xs text-slate-200" />
                            <textarea placeholder="E-posta metni…" value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="rounded-md bg-[#0B0F19] border border-white/10 p-2 text-xs text-slate-200" required />
                            <button type="submit" disabled={loading} className="h-9 rounded-md bg-[#D4AF37] text-slate-900 text-xs font-semibold">Kaydet ve dosya ara</button>
                        </form>
                    </div>
                )}
            </div>

            <div className="grid xl:grid-cols-5 gap-4 min-h-[560px]">
                <div className="xl:col-span-2 flex flex-col rounded-xl border border-white/10 bg-[#121A2F] overflow-hidden min-h-[480px]">
                    <div className="p-3 border-b border-white/10 space-y-2 shrink-0">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Inbox className="h-4 w-4 text-[#D4AF37]" />
                            <span className="font-medium text-slate-200">Gelen kutusu</span>
                            <span className="ml-auto text-slate-500">{items.length} kayıt</span>
                        </div>
                        <div className="relative">
                            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-500" />
                            <input
                                value={searchQ}
                                onChange={(e) => setSearchQ(e.target.value)}
                                placeholder="Konu, gönderen, metin ara…"
                                className="w-full h-8 pl-7 pr-2 rounded-md bg-[#0B0F19] border border-white/10 text-xs text-slate-200"
                            />
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {[{ key: "all", label: "Tümü" }, { key: "hotmail", label: "Hotmail" }, { key: "gmail", label: "Gmail" }].map((f) => (
                                <button key={f.key} type="button" onClick={() => setMailboxFilter(f.key)} className={`px-2 py-0.5 text-[10px] rounded-full border ${mailboxFilter === f.key ? "border-[#D4AF37] text-[#F3E5AB] bg-[#D4AF37]/10" : "border-white/10 text-slate-500"}`}>{f.label}</button>
                            ))}
                        </div>
                        <div className="flex gap-1 border-b border-white/10 -mb-px">
                            {TABS.map((t) => (
                                <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`flex-1 py-1.5 text-[10px] rounded-t ${tab === t.key ? "bg-[#D4AF37]/15 text-[#F3E5AB]" : "text-slate-500"}`}>{t.label}</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 min-h-0">
                        {listLoading ? (
                            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
                        ) : tab === "takip" ? (
                            <ul className="space-y-1">{takipItems.map((tk) => (
                                <li key={tk.id} className="p-2 rounded border border-white/5 text-[10px] text-slate-400">{tk.dosya_no} · {tk.referans_deger}</li>
                            ))}</ul>
                        ) : items.length === 0 ? (
                            <p className="text-xs text-slate-500 text-center py-8">Kayıt yok</p>
                        ) : (
                            <ul className="space-y-1">
                                {items.map((row) => (
                                    <li key={row.id}>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedId(row.id)}
                                            className={`w-full text-left p-2.5 rounded-lg border transition-colors ${selectedId === row.id ? "border-[#D4AF37]/50 bg-[#D4AF37]/10" : "border-white/5 bg-[#0B0F19]/50 hover:border-white/15"}`}
                                        >
                                            <div className="text-xs font-medium text-slate-200 truncate">{row.subject || "(Konu yok)"}</div>
                                            <div className="text-[10px] text-slate-500 truncate mt-0.5">{row.from_email}</div>
                                            <div className="flex flex-wrap gap-1 mt-1.5 items-center">
                                                {row.mailbox && <span className="text-[9px] uppercase px-1 rounded bg-white/5 text-slate-400">{row.mailbox}</span>}
                                                {row.matched_dosya_no && <span className="text-[9px] text-emerald-400 font-mono">{row.matched_dosya_no}</span>}
                                                {row.match_score > 0 && !row.matched_dosya_id && <span className="text-[9px] text-amber-400">{Math.round(row.match_score)}%</span>}
                                            </div>
                                            <div className="text-[10px] text-slate-600 line-clamp-2 mt-1">{row.body_snippet || row.karar_ozeti || ""}</div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                <div className="xl:col-span-3 min-h-[480px]">
                    <EpostaDetailPanel
                        emailId={selectedId}
                        onUpdated={() => { loadOzet(); load(); }}
                        onaylaBagla={onaylaBaglaRow}
                        applyTalepNo={applyTalepNo}
                        applyBusy={applyBusy[selectedId]}
                    />
                </div>
            </div>
                </>
            )}

        </AiPageShell>
    );
}
